import { useEffect, useRef } from 'react'

const SRC_ID    = 'viewshed-src'
const LAYER_ID  = 'viewshed-layer'
const GREEN_RGB = [88, 149, 0]

function removeLayers(map) {
  try { if (map.getLayer(LAYER_ID))  map.removeLayer(LAYER_ID)  } catch (_) {}
  try { if (map.getSource(SRC_ID))   map.removeSource(SRC_ID)   } catch (_) {}
}

function paintViewshedCanvas(viewshed) {
  const { visible, gridWidth, gridHeight, obsGridX, obsGridY, maxDistPx, opacity, showHidden } = viewshed
  const alpha      = Math.round(opacity / 100 * 255)
  const maxDist2   = maxDistPx * maxDistPx

  const canvas = document.createElement('canvas')
  canvas.width  = gridWidth
  canvas.height = gridHeight

  const ctx  = canvas.getContext('2d')
  const img  = ctx.createImageData(gridWidth, gridHeight)
  const data = img.data

  for (let y = 0; y < gridHeight; y++) {
    for (let x = 0; x < gridWidth; x++) {
      const idx  = y * gridWidth + x
      const base = idx * 4
      const dx   = x - obsGridX
      const dy   = y - obsGridY

      if (dx * dx + dy * dy > maxDist2) {
        // Outside circular radius → transparent
        continue
      }

      if (visible[idx] === 1) {
        data[base]     = GREEN_RGB[0]
        data[base + 1] = GREEN_RGB[1]
        data[base + 2] = GREEN_RGB[2]
        data[base + 3] = alpha
      } else if (showHidden) {
        data[base + 3] = alpha  // r/g/b already 0 (black)
      }
      // hidden + !showHidden → transparent (no-op)
    }
  }

  ctx.putImageData(img, 0, 0)
  return canvas
}

// Independent-mode viewshed overlay.
// In combined mode (terrain analysis active) this component renders nothing —
// TerrainOverlay handles the per-tile coloring instead.
export default function ViewshedOverlay({ map, viewshed }) {
  // viewshed: null | { visible, gridWidth, gridHeight, bounds, obsGridX, obsGridY,
  //                    maxDistPx, opacity, showHidden }
  const canvasRef      = useRef(null)
  const boundsRef      = useRef(null)

  // Re-add canvas source after a basemap style change wipes custom layers.
  useEffect(() => {
    if (!map) return
    const onStyleLoad = () => {
      if (!canvasRef.current || !boundsRef.current) return
      try {
        const { west, south, east, north } = boundsRef.current
        map.addSource(SRC_ID, {
          type: 'canvas',
          canvas: canvasRef.current,
          coordinates: [[west, north], [east, north], [east, south], [west, south]],
          animate: false,
        })
        map.addLayer({
          id: LAYER_ID, type: 'raster', source: SRC_ID,
          paint: { 'raster-opacity': 1, 'raster-fade-duration': 0 },
        })
      } catch (_) {}
    }
    map.on('style.load', onStyleLoad)
    return () => map.off('style.load', onStyleLoad)
  }, [map])

  // Re-render canvas whenever viewshed data or display options change.
  useEffect(() => {
    if (!map) return
    removeLayers(map)

    if (!viewshed) {
      canvasRef.current = null
      boundsRef.current = null
      return
    }

    const { bounds } = viewshed
    const { west, south, east, north } = bounds

    const canvas     = paintViewshedCanvas(viewshed)
    canvasRef.current = canvas
    boundsRef.current = bounds

    try {
      map.addSource(SRC_ID, {
        type: 'canvas',
        canvas,
        coordinates: [[west, north], [east, north], [east, south], [west, south]],
        animate: false,
      })
      map.addLayer({
        id: LAYER_ID, type: 'raster', source: SRC_ID,
        paint: { 'raster-opacity': 1, 'raster-fade-duration': 0 },
      })
    } catch (e) {
      console.error('ViewshedOverlay: failed to add layer', e)
    }
  }, [map, viewshed])

  useEffect(() => () => { if (map) removeLayers(map) }, [map])

  return null
}
